import * as Yup from 'yup';
import { COUNTRIES_WITH_GEOLOCATION_INFO } from 'domains/Shipping/utils';
import { CostType } from '../SecondStepForm/components/PickupPointCost/PickupPointCost';

type TimeRange = {
  start: string | undefined;
  end: string | undefined;
  day: string;
  originalIndex?: number;
};

const hasTimeOverlap = (timeRanges: TimeRange[]) => {
  const overlappingIndices = new Set<{ day: string; index: number }>();

  const rangesByDay: Record<string, TimeRange[]> = {};
  timeRanges.forEach((range, index) => {
    if (!rangesByDay[range.day]) {
      rangesByDay[range.day] = [];
    }
    rangesByDay[range.day].push({ ...range, originalIndex: index });
  });

  Object.entries(rangesByDay).forEach(([day, dayRanges]) => {
    dayRanges.sort((a, b) => {
      if (!a.start || !b.start) return 0;
      const [aHours, aMinutes] = a.start.split(':').map(Number);
      const [bHours, bMinutes] = b.start.split(':').map(Number);
      return aHours * 60 + aMinutes - (bHours * 60 + bMinutes);
    });

    for (let i = 0; i < dayRanges.length - 1; i++) {
      const current = dayRanges[i];
      const next = dayRanges[i + 1];

      if (!current.end || !next.start) continue;

      const [currentEndHours, currentEndMinutes] = current.end
        .split(':')
        .map(Number);
      const [nextStartHours, nextStartMinutes] = next.start
        .split(':')
        .map(Number);

      const currentEnd = currentEndHours * 60 + currentEndMinutes;
      const nextStart = nextStartHours * 60 + nextStartMinutes;

      if (currentEnd >= nextStart) {
        overlappingIndices.add({ day, index: next.originalIndex! });
      }
    }
  });

  return Array.from(overlappingIndices);
};

export const validationSchemaSecondStep = Yup.object().shape({
  estimateDeliveryTime: Yup.object().shape({
    maxDays: Yup.string()
      .test('timeMaxRange', 'errors.deliveryTimeMaxLimit', (value) => {
        if (value && (Number(value) < 0 || Number(value) > 365)) {
          return false;
        }
        return true;
      })
      .test('timeMax', 'errors.termMax', (value, { parent: { minDays } }) => {
        if (minDays) {
          return Number(minDays) <= Number(value);
        }
        return true;
      }),
    minDays: Yup.string().test(
      'timeMinRange',
      'errors.deliveryTimeMaxLimit',
      (value) => {
        if (value && (Number(value) < 0 || Number(value) > 365)) {
          return false;
        }
        return true;
      },
    ),
  }),
  cost: Yup.object().shape({
    value: Yup.number()
      .nullable()
      .when('type', {
        is: CostType.FIXED,
        then: (schema) =>
          schema
            .required('errors.requiredValue')
            .moreThan(0, 'errors.valueMustBeGreaterThanZero'),
        otherwise: (schema) => schema.notRequired(),
      }),
  }),
  range: Yup.string()
    .when('country', {
      is: (country: string) => COUNTRIES_WITH_GEOLOCATION_INFO.has(country),
      then: (schema) => schema.required('errors.requiredValue'),
      otherwise: (schema) => schema.notRequired(),
    })
    .test('rangeTest', 'errors.requiredValue', function (value) {
      if (!COUNTRIES_WITH_GEOLOCATION_INFO.has(this.parent.country)) {
        return true;
      }
      if (value && parseFloat(value) >= 10 && parseFloat(value) <= 10000) {
        return true;
      }
      return false;
    }),
  businessHours: Yup.array()
    .of(
      Yup.object()
        .shape({
          start: Yup.string()
            .required('errors.requiredStart')
            .matches(/^([01]?\d|2[0-3]):[0-5]\d$/, 'errors.invalidTimeFormat'),
          end: Yup.string()
            .required('errors.requiredEnd')
            .matches(/^([01]?\d|2[0-3]):[0-5]\d$/, 'errors.invalidTimeFormat')
            .test('endAfterStart', 'errors.endBeforeStart', function (end) {
              const { start } = this.parent;
              if (!start || !end) return true;
              const [startHour, startMinute] = start.split(':').map(Number);
              const [endHour, endMinute] = end.split(':').map(Number);
              return (
                endHour > startHour ||
                (endHour === startHour && endMinute > startMinute)
              );
            }),
        })
        .test(
          'noOverlappingTimes',
          'errors.overlappingTimes',
          function (value) {
            if (!value) return true;

            const allBusinessHours = this.parent;
            const timeRanges = allBusinessHours.map((bh) => ({
              start: bh.start,
              end: bh.end,
              day: bh.day,
            }));

            const overlappingIndices = hasTimeOverlap(timeRanges);
            const overlappingIndexAtDay = overlappingIndices.find(
              (index) => index.day === value.day,
            );

            if (overlappingIndexAtDay) {
              return this.createError({
                path: `businessHours[${overlappingIndexAtDay?.index}].start`,
                message: 'errors.overlappingTimes',
              });
            }

            return true;
          },
        ),
    )
    .min(1, 'errors.businessHoursRequired'),
});
